import { scorePlanogramAesthetics } from "./aestheticsScore";
import { detectExceptions } from "../resolver/detectExceptions";
import type { AestheticsRules } from "./aestheticsRules";
import {
  GeneratedLayout,
  GeneratedPlacement,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  type Flag,
  effectiveAttrs,
  slabPathOfAttrs,
} from "./studioTypes";
import { PATH_SEPARATOR } from "./studioTypes";

export interface VerticalBlockingGrowth {
  skuId: string;
  run: number;
  shelf: number;
  addedFacings: number;
}

export interface VerticalBlockingShrink {
  skuId: string;
  run: number;
  shelf: number;
  removedFacings: number;
}

export interface VerticalBlockingResidual {
  blockKey: string;
  deficitCm: number;
  reason: "floor" | "cap" | "band" | "height";
}

export interface VerticalBlockingFix {
  layout: GeneratedLayout;

  unranged: readonly string[];

  refusedSparse?: { fillRatio: number };
  grown: readonly VerticalBlockingGrowth[];
  shrunk: readonly VerticalBlockingShrink[];
  residuals: readonly VerticalBlockingResidual[];

  raggedBefore: number;
  raggedAfter: number;

  scoreBefore: number;
  scoreAfter: number;
}

export const layoutFromItems = (items: readonly StudioItem[]): GeneratedLayout => ({
  placements: Object.fromEntries(
    items.map((item) => [
      item.skuId,
      {
        skuId: item.skuId,
        segments: item.placements.map((placement) => ({
          run: placement.run,
          shelf: placement.shelf,
          xCm: placement.xCm,
          hFacings: placement.hFacings,
        })),
      },
    ]),
  ),
  walkOrder: items.map((item) => item.skuId),
});

export const slabDepthOf = (draft: StudioDraft): number => {
  let depth = 0;
  for (const level of draft.spine) {
    if (level.orient !== "horizontal") break;
    depth += 1;
  }
  return Math.max(1, Math.min(depth, 3));
};

export const slabBlockKeyOf = (draft: StudioDraft, item: StudioItem): string =>
  slabPathOfAttrs(draft.spine, draft.scopedLevelOrder, effectiveAttrs(item, draft)).values.join(
    PATH_SEPARATOR,
  );

const blockLaneWidths = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  draft: StudioDraft,
): Map<string, Map<string, number>> => {
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const widths = new Map<string, Map<string, number>>();
  for (const placement of Object.values(layout.placements)) {
    const item = bySku.get(placement.skuId);
    if (!item) continue;
    const key = slabBlockKeyOf(draft, item);
    for (const segment of placement.segments) {
      const laneKey = `${segment.run}|${segment.shelf}`;
      const lanes = widths.get(key) ?? new Map<string, number>();
      lanes.set(laneKey, (lanes.get(laneKey) ?? 0) + segment.hFacings * item.facingWidthCm);
      widths.set(key, lanes);
    }
  }
  return widths;
};

/** xCm is an anchor the renderer discards; re-flow left-to-right. */
export const itemsWithLayout = (
  items: readonly StudioItem[],
  layout: GeneratedLayout,
): readonly StudioItem[] => {
  const widthOf = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
  const lanes = new Map<string, { skuId: string; hFacings: number; xCm: number }[]>();
  for (const placement of Object.values(layout.placements)) {
    for (const segment of placement.segments) {
      const laneKey = `${segment.run}|${segment.shelf}`;
      const lane = lanes.get(laneKey) ?? [];
      lane.push({ skuId: placement.skuId, hFacings: segment.hFacings, xCm: segment.xCm });
      lanes.set(laneKey, lane);
    }
  }
  const flowed = new Map<
    string,
    { run: number; shelf: number; xCm: number; widthCm: number; hFacings: number }[]
  >();
  lanes.forEach((lane, laneKey) => {
    const [run, shelf] = laneKey.split("|").map(Number);
    let cursor = 0;
    for (const entry of [...lane].sort((a, b) => a.xCm - b.xCm)) {
      const widthCm = entry.hFacings * (widthOf.get(entry.skuId) ?? 0);
      const list = flowed.get(entry.skuId) ?? [];
      list.push({ run, shelf, xCm: cursor, widthCm, hFacings: entry.hFacings });
      flowed.set(entry.skuId, list);
      cursor += widthCm;
    }
  });
  return items.map((item) => {
    const placements = flowed.get(item.skuId) ?? [];
    return {
      ...item,
      placements,
      facings: placements.reduce((sum, entry) => sum + entry.hFacings, 0),
    };
  });
};

export const blockingScoreOf = (
  items: readonly StudioItem[],
  layout: GeneratedLayout,
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  rules: Partial<AestheticsRules> = {},
  verticalBlockShare?: number,
): number => {
  try {
    // Composite + flags: blocking-only would pass a plan the screen grades worse.
    const projected = itemsWithLayout(items, layout);
    let flags: readonly Flag[] | undefined;
    try {
      flags = detectExceptions(projected, runs, draft, {
        quantityAttribute: rules.sizeAttr,
        blockIdentityAttributes: rules.verticalAttrs,
        verticalBlockShare,
        terminalVariantAttribute: rules.flavourAttr,
      }).flags;
    } catch {
      flags = undefined;
    }
    return scorePlanogramAesthetics({
      items: projected,
      runs,
      draft,
      rules,
      flags,
    }).score;
  } catch {
    // Unmeasurable must not silently pass.
    return Number.NaN;
  }
};

const blockEdgeSpread = (
  layout: GeneratedLayout,
  items: readonly StudioItem[],
  draft: StudioDraft,
): number => {
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  const lanes = new Map<string, Map<string, { lo: number; hi: number }>>();
  for (const placement of Object.values(layout.placements)) {
    const item = bySku.get(placement.skuId);
    if (!item) continue;
    const key = slabBlockKeyOf(draft, item);
    for (const segment of placement.segments) {
      const laneKey = `${segment.run}|${segment.shelf}`;
      const byLane = lanes.get(key) ?? new Map<string, { lo: number; hi: number }>();
      const edge = byLane.get(laneKey);
      const lo = segment.xCm;
      const hi = segment.xCm + segment.hFacings * item.facingWidthCm;
      byLane.set(
        laneKey,
        edge ? { lo: Math.min(edge.lo, lo), hi: Math.max(edge.hi, hi) } : { lo, hi },
      );
      lanes.set(key, byLane);
    }
  }
  let total = 0;
  lanes.forEach((byLane) => {
    if (byLane.size < 2) return;
    const los = [...byLane.values()].map((edge) => edge.lo);
    const his = [...byLane.values()].map((edge) => edge.hi);
    total += Math.max(...los) - Math.min(...los) + (Math.max(...his) - Math.min(...his));
  });
  return total;
};

const raggedness = (widths: ReadonlyMap<string, Map<string, number>>): number => {
  let total = 0;
  widths.forEach((lanes) => {
    if (lanes.size < 2) return;
    const values = [...lanes.values()];
    total += Math.max(...values) - Math.min(...values);
  });
  return total;
};

const COLUMN_OVERLAP = 0.5;

export interface FixVerticalBlockingOptions {
  minFillRatio?: number;

  preserveRange?: boolean;

  onlyIfBetter?: boolean;

  rules?: Partial<AestheticsRules>;

  verticalBlockShare?: number;
}

export const fixVerticalBlocking = (
  items: readonly StudioItem[],
  layout: GeneratedLayout,
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: FixVerticalBlockingOptions = {},
): VerticalBlockingFix => {
  const preserveRange = options.preserveRange ?? true;
  const onlyIfBetter = options.onlyIfBetter ?? true;
  const raggedBefore = raggedness(blockLaneWidths(layout, items, draft));

  {
    const itemWidth = new Map(items.map((item) => [item.skuId, item.facingWidthCm]));
    let productCm = 0;
    for (const placement of Object.values(layout.placements)) {
      const w = itemWidth.get(placement.skuId) ?? 0;
      for (const segment of placement.segments) productCm += segment.hFacings * w;
    }
    const shelfCm = runs.reduce((sum, run) => sum + run.widthCm * run.shelves.length, 0);
    const fillRatio = shelfCm > 0 ? productCm / shelfCm : 0;
    if (fillRatio < (options.minFillRatio ?? 0.9)) {
      return {
        layout,
        refusedSparse: { fillRatio },
        unranged: [],
        grown: [],
        shrunk: [],
        residuals: [],
        raggedBefore,
        raggedAfter: raggedBefore,
        scoreBefore: Number.NaN,
        scoreAfter: Number.NaN,
      };
    }
  }
  const facingsOf = (target: GeneratedLayout): Map<string, number> => {
    const map = new Map<string, number>();
    for (const placement of Object.values(target.placements)) {
      for (const segment of placement.segments) {
        map.set(placement.skuId, (map.get(placement.skuId) ?? 0) + segment.hFacings);
      }
    }
    return map;
  };
  const before = facingsOf(layout);

  const spineAttrs = draft.spine.map((level) => level.attr);
  const keyOf = (item: StudioItem): string => slabBlockKeyOf(draft, item);

  const fullKeyOf = (item: StudioItem): string =>
    spineAttrs.map((attr) => effectiveAttrs(item, draft)[attr] ?? "").join(PATH_SEPARATOR);
  const bySku = new Map(items.map((item) => [item.skuId, item]));

  const residuals: VerticalBlockingResidual[] = [];
  const allUnranged = new Set<string>();
  const placements: Record<string, GeneratedPlacement> = {};
  const place = (skuId: string, run: number, shelfRank: number, xCm: number, hFacings: number) => {
    if (hFacings <= 0) return;
    const existing = placements[skuId];
    const segment = { run, shelf: shelfRank, xCm: +xCm.toFixed(1), hFacings };
    placements[skuId] = existing
      ? { skuId, segments: [...existing.segments, segment] }
      : { skuId, segments: [segment] };
  };

  type Pack = {
    skuId: string;
    w: number;
    h: number;
    hFacings: number;
    xCm: number;
    dos: number;
    sales: number;
    maxFacings: number;

    fullKey: string;
  };
  type Chunk = { packs: Pack[]; startX: number; gapBefore: number };
  type Block = { key: string; chunks: Map<number, Chunk> };

  for (const run of runs) {
    const byRank = [...run.shelves].sort((a, b) => a.rank - b.rank);
    const openings = new Map<number, number>();
    for (const shelf of byRank) {
      const above = byRank.find((candidate) => candidate.rank === shelf.rank - 1);
      const ceiling = above ? above.yCm - above.thicknessCm : run.heightCm || Infinity;
      openings.set(shelf.rank, Math.max(ceiling - shelf.yCm, 0) || Infinity);
    }

    const lanePacks = new Map<number, (Pack & { key: string })[]>();
    for (const placement of Object.values(layout.placements)) {
      const item = bySku.get(placement.skuId);
      if (!item || item.run !== run.run) continue;
      for (const segment of placement.segments) {
        if (segment.run !== run.run || segment.hFacings <= 0) continue;
        const list = lanePacks.get(segment.shelf) ?? [];
        list.push({
          skuId: placement.skuId,
          key: keyOf(item),
          w: item.facingWidthCm,
          h: item.heightCm || 0,
          hFacings: segment.hFacings,
          xCm: segment.xCm,
          dos: item.dosPerFacing ?? 0,
          sales: item.salesAmount ?? 0,
          maxFacings: item.maxFacings ?? Number.POSITIVE_INFINITY,
          fullKey: fullKeyOf(item),
        });
        lanePacks.set(segment.shelf, list);
      }
    }
    lanePacks.forEach((list) => list.sort((a, b) => a.xCm - b.xCm));

    const blocks = new Map<string, Block>();
    lanePacks.forEach((list, shelfRank) => {
      for (const pack of list) {
        const block = blocks.get(pack.key) ?? { key: pack.key, chunks: new Map<number, Chunk>() };
        const chunk = block.chunks.get(shelfRank) ?? { packs: [], startX: pack.xCm, gapBefore: 0 };
        chunk.packs.push(pack);
        chunk.startX = Math.min(chunk.startX, pack.xCm);
        block.chunks.set(shelfRank, chunk);
        blocks.set(pack.key, block);
      }
    });

    const chunkWidth = (chunk: Chunk) =>
      chunk.packs.reduce((sum, pack) => sum + pack.w * pack.hFacings, 0);

    blocks.forEach((block) =>
      block.chunks.forEach((chunk, shelfRank) => {
        const list = lanePacks.get(shelfRank) ?? [];
        let prevEnd = 0;
        for (const pack of list) {
          if (pack.xCm >= chunk.startX - 0.01) break;
          prevEnd = Math.max(prevEnd, pack.xCm + pack.w * pack.hFacings);
        }
        chunk.gapBefore = Math.max(chunk.startX - prevEnd, 0);
      }),
    );

    const bandWidthOf = new Map<string, number>();

    const blockWidth0 = new Map<string, number>();
    const startAnchorOf = new Map<string, number>();
    blocks.forEach((block) => {
      const widths = [...block.chunks.values()].map(chunkWidth);
      bandWidthOf.set(block.key, widths.reduce((sum, value) => sum + value, 0) / widths.length);
      blockWidth0.set(
        block.key,
        widths.reduce((sum, value) => sum + value, 0),
      );
      const starts = [...block.chunks.values()].map((chunk) => chunk.startX).sort((a, b) => a - b);
      startAnchorOf.set(block.key, starts[Math.floor((starts.length - 1) / 2)]);
    });
    const skuTotals = new Map<string, number>();
    blocks.forEach((block) =>
      block.chunks.forEach((chunk) =>
        chunk.packs.forEach((pack) =>
          skuTotals.set(pack.skuId, (skuTotals.get(pack.skuId) ?? 0) + pack.hFacings),
        ),
      ),
    );

    const laneTotal = new Map<number, number>();

    const laneCap = new Map<number, number>();
    const recomputeLaneTotals = () => {
      laneTotal.clear();
      blocks.forEach((block) =>
        block.chunks.forEach((chunk, shelfRank) =>
          laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + chunkWidth(chunk)),
        ),
      );
    };
    recomputeLaneTotals();
    laneTotal.forEach((_, shelfRank) => laneCap.set(shelfRank, run.widthCm));
    const rebalancePass = () => {
      blocks.forEach((block) => {
        if (block.chunks.size < 2) return;
        const band = bandWidthOf.get(block.key) ?? 0;
        for (let guard = 0; guard < 500; guard++) {
          const entries = [...block.chunks.entries()].sort(
            (a, b) => chunkWidth(b[1]) - chunkWidth(a[1]),
          );
          const [srcRank, src] = entries[0];
          let moved = false;
          for (let packIndex = src.packs.length - 1; packIndex >= 0 && !moved; packIndex--) {
            const pack = src.packs[packIndex];
            for (let target = entries.length - 1; target >= 1 && !moved; target--) {
              const [dstRank, dst] = entries[target];

              const beforeDev = Math.abs(chunkWidth(src) - band) + Math.abs(chunkWidth(dst) - band);
              const afterDev =
                Math.abs(chunkWidth(src) - pack.w - band) +
                Math.abs(chunkWidth(dst) + pack.w - band);
              if (afterDev >= beforeDev - 0.01) continue;
              if ((openings.get(dstRank) ?? Infinity) + 0.5 < pack.h) continue;
              if (
                (laneTotal.get(dstRank) ?? 0) + pack.w >
                (laneCap.get(dstRank) ?? run.widthCm) + 0.5
              )
                continue;

              const kinIndex = dst.packs.reduce(
                (found, candidate, index) => (candidate.fullKey === pack.fullKey ? index : found),
                -1,
              );
              if (kinIndex < 0) continue;
              pack.hFacings -= 1;
              if (pack.hFacings <= 0) src.packs.splice(packIndex, 1);
              const kin = dst.packs[kinIndex];
              if (kin.skuId === pack.skuId) kin.hFacings += 1;
              else dst.packs.splice(kinIndex + 1, 0, { ...pack, hFacings: 1 });
              laneTotal.set(srcRank, (laneTotal.get(srcRank) ?? 0) - pack.w);
              laneTotal.set(dstRank, (laneTotal.get(dstRank) ?? 0) + pack.w);
              moved = true;
            }
          }
          if (!moved) break;
        }
      });
    };

    const prefixOf = (key: string) => key.split(PATH_SEPARATOR).slice(0, 2).join(PATH_SEPARATOR);
    const laneOrder = new Map<number, Block[]>();
    const laneAir = new Map<number, number>();
    {
      const perLane = new Map<number, { block: Block; startX: number }[]>();
      blocks.forEach((block) =>
        block.chunks.forEach((chunk, shelfRank) => {
          const list = perLane.get(shelfRank) ?? [];
          list.push({ block, startX: chunk.startX });
          perLane.set(shelfRank, list);
        }),
      );
      perLane.forEach((list, shelfRank) => {
        list.sort((a, b) => a.startX - b.startX);
        laneOrder.set(
          shelfRank,
          list.map((entry) => entry.block),
        );
        laneAir.set(shelfRank, Math.max(list[0]?.startX ?? 0, 0));
      });
    }
    const seamOf = (block: Block, shelfRank: number): number => {
      const order = laneOrder.get(shelfRank) ?? [];
      let x = laneAir.get(shelfRank) ?? 0;
      for (const candidate of order) {
        const chunk = candidate.chunks.get(shelfRank);
        if (chunk) x += chunkWidth(chunk);
        if (candidate === block) break;
      }
      return x;
    };
    const unrangedSkus = allUnranged;

    const mayDrop = (pack: Pack): boolean =>
      !preserveRange || (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings > 0;

    const mayDropAll = (packs: readonly Pack[]): boolean => {
      if (!preserveRange) return true;
      const draining = new Map<string, number>();
      for (const pack of packs)
        draining.set(pack.skuId, (draining.get(pack.skuId) ?? 0) + pack.hFacings);
      return [...draining].every(([skuId, going]) => (skuTotals.get(skuId) ?? 0) - going > 0);
    };

    const mayEmptyChunk = (block: Block, shelfRank: number): boolean => {
      const brand = prefixOf(block.key);
      return [...blocks.values()].some(
        (candidate) =>
          candidate !== block &&
          prefixOf(candidate.key) === brand &&
          (candidate.chunks.get(shelfRank)?.packs.length ?? 0) > 0,
      );
    };

    const blockWidthNow = (block: Block): number =>
      [...block.chunks.values()].reduce((sum, chunk) => sum + chunkWidth(chunk), 0);
    const corridorOf = (block: Block): number => {
      const widestPack = Math.max(
        ...[...block.chunks.values()].flatMap((chunk) => chunk.packs.map((pack) => pack.w)),
        0,
      );
      return Math.max((blockWidth0.get(block.key) ?? 0) * 0.15, widestPack);
    };
    const mayGrow = (block: Block, addCm: number): boolean =>
      blockWidthNow(block) + addCm - (blockWidth0.get(block.key) ?? 0) <= corridorOf(block);
    const mayShrink = (block: Block, removeCm: number): boolean =>
      (blockWidth0.get(block.key) ?? 0) - (blockWidthNow(block) - removeCm) <= corridorOf(block);
    const deviationOf = new Map<string, number>();
    const TOLERANCE_CM = 2;

    const seamTargets = new Map<string, number>();

    const columnIdOf = new Map<string, string>();
    {
      const spanOfBlock = (block: Block) => {
        const shelves = [...block.chunks.keys()];
        const los = shelves.map((rank) => block.chunks.get(rank)?.startX ?? 0);
        const his = shelves.map((rank) => seamOf(block, rank));
        return { lo: Math.min(...los), hi: Math.max(...his) };
      };
      const list = [...blocks.values()].map((block) => ({ block, span: spanOfBlock(block) }));
      const parent = list.map((_, index) => index);
      const find = (index: number): number => {
        let root = index;
        while (parent[root] !== root) root = parent[root];
        return root;
      };
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const a = list[i].span;
          const b = list[j].span;
          const shared = Math.min(a.hi, b.hi) - Math.max(a.lo, b.lo);
          if (shared <= 0) continue;
          const narrower = Math.min(a.hi - a.lo, b.hi - b.lo);
          if (narrower <= 0) continue;

          const sharedShelves = [...list[i].block.chunks.keys()].filter((rank) =>
            list[j].block.chunks.has(rank),
          ).length;
          // Stacked, not side-by-side: shared shelves means neighbours, not a column.
          if (shared / narrower >= COLUMN_OVERLAP && sharedShelves === 0) {
            const rootA = find(i);
            const rootB = find(j);
            if (rootA !== rootB) parent[rootB] = rootA;
          }
        }
      }
      list.forEach((entry, index) => columnIdOf.set(entry.block.key, `col:${find(index)}`));
    }

    const alignSeams = () => {
      const blockOrder = [...blocks.values()].sort(
        (a, b) => (startAnchorOf.get(a.key) ?? 0) - (startAnchorOf.get(b.key) ?? 0),
      );

      const columnSeams = (block: Block): number[] => {
        const columnId = columnIdOf.get(block.key);
        const seams: number[] = [];
        blocks.forEach((candidate) => {
          if (columnIdOf.get(candidate.key) !== columnId) return;
          candidate.chunks.forEach((_chunk, shelfRank) => seams.push(seamOf(candidate, shelfRank)));
        });
        return seams;
      };
      for (const block of blockOrder) {
        const seams = columnSeams(block);
        if (seams.length < 2) continue;
        const sorted = [...seams].sort((a, b) => a - b);

        const computed = sorted[Math.floor((sorted.length - 1) / 2)];
        const targetKey = columnIdOf.get(block.key) ?? block.key;
        if (!seamTargets.has(targetKey)) seamTargets.set(targetKey, computed);
        const consensus = seamTargets.get(targetKey) ?? computed;
        block.chunks.forEach((chunk, shelfRank) => {
          const cap = laneCap.get(shelfRank) ?? run.widthCm;

          for (let guard = 0; guard < 500; guard++) {
            const err = seamOf(block, shelfRank) - consensus;
            if (Math.abs(err) <= TOLERANCE_CM) break;
            if (err < 0) {
              const pick = chunk.packs
                .filter(
                  (pack) =>
                    (skuTotals.get(pack.skuId) ?? 0) < pack.maxFacings &&
                    -err > pack.w / 2 &&
                    (laneTotal.get(shelfRank) ?? 0) + pack.w <= cap + 0.5 &&
                    mayGrow(block, pack.w),
                )
                .sort(
                  (a, b) =>
                    (skuTotals.get(a.skuId) ?? 0) - (skuTotals.get(b.skuId) ?? 0) ||
                    b.sales - a.sales,
                )[0];
              if (!pick) break;
              pick.hFacings += 1;
              skuTotals.set(pick.skuId, (skuTotals.get(pick.skuId) ?? 0) + 1);
              laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + pick.w);
            } else {
              const pick = chunk.packs
                .filter((pack) => pack.hFacings > 1 && err > pack.w / 2)
                .sort(
                  (a, b) =>
                    a.sales - b.sales ||
                    (skuTotals.get(b.skuId) ?? 0) - (skuTotals.get(a.skuId) ?? 0),
                )[0];
              if (!pick) break;
              pick.hFacings -= 1;
              skuTotals.set(pick.skuId, (skuTotals.get(pick.skuId) ?? 0) - 1);
              laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pick.w);
            }
          }

          for (let guard = 0; guard < 200; guard++) {
            const err = seamOf(block, shelfRank) - consensus;
            if (Math.abs(err) <= TOLERANCE_CM) break;
            let best: { grow: Pack; shrink: Pack; newErr: number } | null = null;
            for (const grow of chunk.packs) {
              if ((skuTotals.get(grow.skuId) ?? 0) >= grow.maxFacings) continue;
              for (const shrink of chunk.packs) {
                if (shrink.hFacings <= 1 || shrink === grow) continue;
                if ((laneTotal.get(shelfRank) ?? 0) + grow.w - shrink.w > cap + 0.5) continue;
                const newErr = err + grow.w - shrink.w;
                if (Math.abs(newErr) < Math.abs(best ? best.newErr : err) - 0.01) {
                  best = { grow, shrink, newErr };
                }
              }
            }
            if (!best) break;
            best.grow.hFacings += 1;
            skuTotals.set(best.grow.skuId, (skuTotals.get(best.grow.skuId) ?? 0) + 1);
            best.shrink.hFacings -= 1;
            skuTotals.set(best.shrink.skuId, (skuTotals.get(best.shrink.skuId) ?? 0) - 1);
            laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + best.grow.w - best.shrink.w);
          }

          for (let guard = 0; guard < 20; guard++) {
            const err = seamOf(block, shelfRank) - consensus;
            if (err <= TOLERANCE_CM) break;
            if (chunk.packs.length <= 1 && !mayEmptyChunk(block, shelfRank)) break;
            const pick = [...chunk.packs]
              .filter((pack) => Math.abs(err - pack.w * pack.hFacings) < Math.abs(err))
              .filter((pack) => mayShrink(block, pack.w * pack.hFacings))
              .filter(mayDrop)
              .sort(
                (a, b) =>
                  a.sales - b.sales ||
                  (skuTotals.get(b.skuId) ?? 0) - (skuTotals.get(a.skuId) ?? 0),
              )[0];
            if (!pick) break;
            chunk.packs.splice(chunk.packs.indexOf(pick), 1);
            const total = (skuTotals.get(pick.skuId) ?? 0) - pick.hFacings;
            skuTotals.set(pick.skuId, total);
            laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pick.w * pick.hFacings);
            if (total <= 0) unrangedSkus.add(pick.skuId);
          }
          const err = Math.abs(seamOf(block, shelfRank) - consensus);
          deviationOf.set(block.key, Math.max(deviationOf.get(block.key) ?? 0, err));
        });
      }
    };
    for (let pass = 0; pass < 2; pass++) {
      recomputeLaneTotals();
      rebalancePass();
      deviationOf.clear();
      alignSeams();
    }

    const shelfRanks = new Set<number>();
    blocks.forEach((block) => block.chunks.forEach((_, shelfRank) => shelfRanks.add(shelfRank)));
    type Placed = { block: Block; chunk: Chunk; shelfRank: number; x: number; squeeze: number };
    const computePlacements = (): Placed[] => {
      const out: Placed[] = [];
      shelfRanks.forEach((shelfRank) => {
        const chunks = [...blocks.values()]
          .map((block) => ({ block, chunk: block.chunks.get(shelfRank) }))
          .filter((entry): entry is { block: Block; chunk: Chunk } => !!entry.chunk)
          .sort((a, b) => a.chunk.startX - b.chunk.startX);
        const widths = chunks.map((entry) => chunkWidth(entry.chunk));
        const total = widths.reduce((sum, value) => sum + value, 0);
        const squeeze = total > run.widthCm && total > 0 ? run.widthCm / total : 1;
        const tailWidth = widths.map((_, index) =>
          widths.slice(index).reduce((sum, value) => sum + value, 0),
        );

        let cursor = 0;
        chunks.forEach((entry, index) => {
          const latestStart = run.widthCm - tailWidth[index] * squeeze;
          const anchor = startAnchorOf.get(entry.block.key) ?? entry.chunk.startX;

          const airCap = cursor + entry.chunk.gapBefore;
          const x = Math.max(cursor, Math.min(anchor, latestStart, airCap));
          out.push({ block: entry.block, chunk: entry.chunk, shelfRank, x, squeeze });
          cursor = x + chunkWidth(entry.chunk) * squeeze;
        });
      });
      return out;
    };

    const SAME_STRETCH = 12;
    type Piece = { shelfRank: number; lo: number; hi: number };
    const stretchCount = (pieces: Piece[], foreign: Piece[]): number => {
      if (!pieces.length) return 0;
      const lanesUsed = new Set(pieces.map((piece) => piece.shelfRank));
      const others = foreign.filter((piece) => lanesUsed.has(piece.shelfRank));
      const sorted = [...pieces].sort((a, b) => a.lo - b.lo);
      let count = 0;
      let reach = -Infinity;
      for (const piece of sorted) {
        const gap = piece.lo - reach;
        if (count === 0) count = 1;
        else if (gap > SAME_STRETCH) {
          const intruder = others.some(
            (other) => other.lo < piece.lo - 0.5 && other.hi > reach + 0.5,
          );
          if (intruder) count += 1;
        }
        reach = Math.max(reach, piece.hi);
      }
      return count;
    };

    const asBuiltStretches = new Map<string, number>();
    {
      const byPrefix = new Map<string, Piece[]>();
      const all: (Piece & { prefix: string })[] = [];
      for (const placement of Object.values(layout.placements)) {
        const item = bySku.get(placement.skuId);
        if (!item || item.run !== run.run) continue;
        const prefix = prefixOf(keyOf(item));
        for (const segment of placement.segments) {
          if (segment.run !== run.run || segment.hFacings <= 0) continue;
          const piece = {
            prefix,
            shelfRank: segment.shelf,
            lo: segment.xCm,
            hi: segment.xCm + segment.hFacings * item.facingWidthCm,
          };
          const bucket = byPrefix.get(prefix) ?? [];
          bucket.push(piece);
          byPrefix.set(prefix, bucket);
          all.push(piece);
        }
      }
      byPrefix.forEach((bucket, prefix) =>
        asBuiltStretches.set(
          prefix,
          stretchCount(
            bucket,
            all.filter((piece) => piece.prefix !== prefix),
          ),
        ),
      );
    }

    const trimBudget = new Map<Block, number>();
    let placed = computePlacements();

    for (let guard = 0; guard < 400; guard++) {
      recomputeLaneTotals();
      let overfull: number | null = null;
      laneTotal.forEach((total, shelfRank) => {
        if (total > run.widthCm + 0.5 && overfull === null) overfull = shelfRank;
      });
      if (overfull === null) break;
      const laneChunks = placed.filter((entry) => entry.shelfRank === overfull);
      const shrinkable = laneChunks
        .flatMap((entry) => entry.chunk.packs.map((pack) => ({ entry, pack })))
        .filter(({ pack }) => pack.hFacings > 1)
        .sort((a, b) => a.pack.sales - b.pack.sales)[0];
      if (shrinkable) {
        shrinkable.pack.hFacings -= 1;
        skuTotals.set(shrinkable.pack.skuId, (skuTotals.get(shrinkable.pack.skuId) ?? 0) - 1);
        continue;
      }
      const removable = laneChunks
        .flatMap((entry) => entry.chunk.packs.map((pack) => ({ entry, pack })))
        .filter(
          ({ entry }) =>
            entry.chunk.packs.length > 1 || mayEmptyChunk(entry.block, entry.shelfRank),
        )

        // Overflow is exempt from the range floor; still prefer a SKU that survives.
        .sort(
          (a, b) =>
            Number(mayDrop(b.pack)) - Number(mayDrop(a.pack)) || a.pack.sales - b.pack.sales,
        )[0];
      if (!removable) {
        residuals.push({
          blockKey: `run fit · shelf ${overfull}`,
          deficitCm: +((laneTotal.get(overfull) ?? 0) - run.widthCm).toFixed(1),
          reason: "cap",
        });
        break;
      }
      const { entry, pack } = removable;
      entry.chunk.packs.splice(entry.chunk.packs.indexOf(pack), 1);
      const totalLeft = (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings;
      skuTotals.set(pack.skuId, totalLeft);
      if (totalLeft <= 0) unrangedSkus.add(pack.skuId);
      if (!entry.chunk.packs.length) entry.block.chunks.delete(entry.shelfRank);
    }
    placed = computePlacements();
    for (let round = 0; round < 8; round++) {
      const intervals = new Map<
        string,
        { shelfRank: number; lo: number; hi: number; entry: Placed }[]
      >();
      const allPieces: (Piece & { prefix: string })[] = [];
      for (const entry of placed) {
        const prefix = prefixOf(entry.block.key);
        const piece = {
          prefix,
          shelfRank: entry.shelfRank,
          lo: entry.x,
          hi: entry.x + chunkWidth(entry.chunk) * entry.squeeze,
          entry,
        };
        const bucket = intervals.get(prefix) ?? [];
        bucket.push(piece);
        intervals.set(prefix, bucket);
        allPieces.push(piece);
      }
      let changed = false;
      intervals.forEach((bucket, prefix) => {
        const count = stretchCount(
          bucket,
          allPieces.filter((piece) => piece.prefix !== prefix),
        );
        if (count <= (asBuiltStretches.get(prefix) ?? 1)) return;

        const sorted = [...bucket].sort((a, b) => a.lo - b.lo);
        const components: (typeof sorted)[] = [];
        let reach = -Infinity;
        for (const piece of sorted) {
          if (piece.lo > reach + 0.6) components.push([]);
          components[components.length - 1].push(piece);
          reach = Math.max(reach, piece.hi);
        }
        const bySize = [...components].sort(
          (a, b) =>
            a.reduce((sum, piece) => sum + piece.hi - piece.lo, 0) -
            b.reduce((sum, piece) => sum + piece.hi - piece.lo, 0),
        );
        const stray = bySize[0];
        const main = bySize[bySize.length - 1];
        const mainLo = Math.min(...main.map((piece) => piece.lo));
        const mainHi = Math.max(...main.map((piece) => piece.hi));
        const strayLo = Math.min(...stray.map((piece) => piece.lo));

        const straySkus = new Set(
          stray.flatMap((piece) => piece.entry.chunk.packs.map((pack) => pack.skuId)),
        );
        if (
          straySkus.size === 1 &&
          stray.every((piece) => piece.entry.chunk.packs.length > 0) &&
          mayDropAll(stray.flatMap((piece) => piece.entry.chunk.packs))
        ) {
          for (const piece of stray) {
            const { chunk, shelfRank } = piece.entry;
            for (const pack of [...chunk.packs]) {
              chunk.packs.splice(chunk.packs.indexOf(pack), 1);
              const totalLeft = (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings;
              skuTotals.set(pack.skuId, totalLeft);
              laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pack.w * pack.hFacings);
              if (totalLeft <= 0) unrangedSkus.add(pack.skuId);
            }
          }
          changed = true;
          return;
        }

        if (strayLo > mainHi + 0.5) {
          let reach = mainHi;
          for (let guard = 0; guard < 50 && reach < strayLo - 0.5; guard++) {
            const frontier = [...main].sort((a, b) => b.hi - a.hi)[0];
            const { chunk, shelfRank } = frontier.entry;
            const cap = laneCap.get(shelfRank) ?? run.widthCm;
            const pick = [...chunk.packs]
              .filter(
                (pack) =>
                  (skuTotals.get(pack.skuId) ?? 0) < pack.maxFacings &&
                  (laneTotal.get(shelfRank) ?? 0) + pack.w <= cap + 0.5 &&
                  mayGrow(frontier.entry.block, pack.w),
              )
              .sort(
                (a, b) =>
                  (skuTotals.get(a.skuId) ?? 0) - (skuTotals.get(b.skuId) ?? 0) ||
                  b.sales - a.sales,
              )[0];
            if (!pick) break;
            pick.hFacings += 1;
            skuTotals.set(pick.skuId, (skuTotals.get(pick.skuId) ?? 0) + 1);
            laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + pick.w);
            frontier.hi += pick.w;
            reach = Math.max(reach, frontier.hi);
            changed = true;
          }
          if (reach < strayLo - 0.5) {
            for (const piece of stray) {
              const { chunk, shelfRank } = piece.entry;
              if (!mayEmptyChunk(piece.entry.block, shelfRank)) continue;
              if (!mayDropAll(chunk.packs)) continue;
              for (const pack of [...chunk.packs]) {
                chunk.packs.splice(chunk.packs.indexOf(pack), 1);
                const totalLeft = (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings;
                skuTotals.set(pack.skuId, totalLeft);
                laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pack.w * pack.hFacings);
                if (totalLeft <= 0) unrangedSkus.add(pack.skuId);
              }
              changed = true;
            }
          }
        }
        for (const piece of stray) {
          const { chunk, shelfRank } = piece.entry;
          const cap = laneCap.get(shelfRank) ?? run.widthCm;

          let guard = 0;
          while (piece.hi < mainLo - 0.5 && guard++ < 50) {
            const pick = [...chunk.packs]
              .filter(
                (pack) =>
                  (skuTotals.get(pack.skuId) ?? 0) < pack.maxFacings &&
                  (laneTotal.get(shelfRank) ?? 0) + pack.w <= cap + 0.5 &&
                  mayGrow(piece.entry.block, pack.w),
              )
              .sort(
                (a, b) =>
                  (skuTotals.get(a.skuId) ?? 0) - (skuTotals.get(b.skuId) ?? 0) ||
                  b.sales - a.sales,
              )[0];
            if (!pick) break;
            pick.hFacings += 1;
            skuTotals.set(pick.skuId, (skuTotals.get(pick.skuId) ?? 0) + 1);
            laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + pick.w);
            piece.hi += pick.w;
            changed = true;
          }
          if (
            piece.hi < mainLo - 0.5 &&
            mayEmptyChunk(piece.entry.block, shelfRank) &&
            mayDropAll(chunk.packs)
          ) {
            for (const pack of [...chunk.packs]) {
              chunk.packs.splice(chunk.packs.indexOf(pack), 1);
              const totalLeft = (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings;
              skuTotals.set(pack.skuId, totalLeft);
              laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pack.w * pack.hFacings);
              if (totalLeft <= 0) unrangedSkus.add(pack.skuId);
            }
            changed = true;
          }
        }
      });

      {
        const byBlock = new Map<
          Block,
          { shelfRank: number; lo: number; hi: number; chunk: Chunk }[]
        >();
        for (const entry of placed) {
          const bucket = byBlock.get(entry.block) ?? [];
          bucket.push({
            shelfRank: entry.shelfRank,
            lo: entry.x,
            hi: entry.x + chunkWidth(entry.chunk) * entry.squeeze,
            chunk: entry.chunk,
          });
          byBlock.set(entry.block, bucket);
        }
        byBlock.forEach((pieces, block) => {
          if (pieces.length < 2 || block.chunks.size <= 1) return;
          const spreadOf = (list: { lo: number; hi: number }[]) =>
            Math.max(...list.map((piece) => piece.lo)) -
            Math.min(...list.map((piece) => piece.lo)) +
            (Math.max(...list.map((piece) => piece.hi)) -
              Math.min(...list.map((piece) => piece.hi)));
          const maxPack = Math.max(
            ...[...block.chunks.values()].flatMap((chunk) => chunk.packs.map((pack) => pack.w)),
            0,
          );
          const spread = spreadOf(pieces);
          if (spread <= maxPack + 0.5) return;

          let best: { piece: (typeof pieces)[number]; spread: number } | null = null;
          for (const piece of pieces) {
            const rest = pieces.filter((candidate) => candidate !== piece);
            const restSpread = spreadOf(rest);
            if (restSpread < spread - 0.5 && (!best || restSpread < best.spread)) {
              best = { piece, spread: restSpread };
            }
          }
          if (!best) return;
          const spent = trimBudget.get(block) ?? 0;
          if (spent >= 2) return;
          const { chunk, shelfRank, lo, hi } = best.piece;
          const rest = pieces.filter((candidate) => candidate !== best!.piece);
          const medianOf = (values: number[]) => {
            const sorted = [...values].sort((a, b) => a - b);
            return sorted[Math.floor((sorted.length - 1) / 2)];
          };
          const loDev = Math.abs(lo - medianOf(rest.map((piece) => piece.lo)));
          const hiDev = Math.abs(hi - medianOf(rest.map((piece) => piece.hi)));
          const dropPack = (pack: Pack) => {
            chunk.packs.splice(chunk.packs.indexOf(pack), 1);
            const totalLeft = (skuTotals.get(pack.skuId) ?? 0) - pack.hFacings;
            skuTotals.set(pack.skuId, totalLeft);
            laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) - pack.w * pack.hFacings);
            if (totalLeft <= 0) unrangedSkus.add(pack.skuId);
          };
          const budgetLeft = 2 - spent;
          const distinctSkus = new Set(chunk.packs.map((pack) => pack.skuId)).size;
          const laneWidth = chunkWidth(chunk);
          if (
            loDev > hiDev &&
            distinctSkus <= budgetLeft &&
            mayEmptyChunk(block, shelfRank) &&
            mayShrink(block, laneWidth) &&
            mayDropAll(chunk.packs)
          ) {
            trimBudget.set(block, spent + distinctSkus);
            for (const pack of [...chunk.packs]) dropPack(pack);
          } else {
            if (chunk.packs.length <= 1 && !mayEmptyChunk(block, shelfRank)) return;
            const pick = [...chunk.packs]
              .filter((pack) => mayShrink(block, pack.w * pack.hFacings))
              .filter(mayDrop)
              .sort(
                (a, b) =>
                  a.sales - b.sales ||
                  (skuTotals.get(b.skuId) ?? 0) - (skuTotals.get(a.skuId) ?? 0),
              )[0];
            if (!pick) return;
            trimBudget.set(block, spent + 1);
            dropPack(pick);
          }
          if (!chunk.packs.length) block.chunks.delete(shelfRank);
          changed = true;
        });
      }
      if (!changed) break;

      recomputeLaneTotals();
      deviationOf.clear();
      alignSeams();
      placed = computePlacements();
    }

    const extraGrown = new Map<Chunk, number>();
    for (let guard = 0; guard < 400; guard++) {
      const laneEnd = new Map<number, number>();
      const lastChunks = new Map<number, { entry: Placed; hi: number }[]>();
      for (const entry of placed) {
        const hi = entry.x + chunkWidth(entry.chunk) * entry.squeeze;
        laneEnd.set(entry.shelfRank, Math.max(laneEnd.get(entry.shelfRank) ?? 0, hi));
        const list = lastChunks.get(entry.shelfRank) ?? [];
        list.push({ entry, hi });
        lastChunks.set(entry.shelfRank, list);
      }
      let grewAny = false;
      laneEnd.forEach((end, shelfRank) => {
        const deficit = run.widthCm - end;
        if (deficit < 2) return;
        const ordered = (lastChunks.get(shelfRank) ?? []).sort((a, b) => b.hi - a.hi);

        const chunksBack = ordered.length > 1 ? ordered.slice(1) : ordered;
        for (const { entry } of chunksBack) {
          const pick = [...entry.chunk.packs]
            .filter(
              (pack) =>
                (skuTotals.get(pack.skuId) ?? 0) < pack.maxFacings &&
                pack.w <= deficit + pack.w / 2 &&
                (laneTotal.get(shelfRank) ?? 0) + pack.w <= run.widthCm + 0.5 &&
                mayGrow(entry.block, pack.w),
            )
            .sort(
              (a, b) =>
                (skuTotals.get(a.skuId) ?? 0) - (skuTotals.get(b.skuId) ?? 0) || b.sales - a.sales,
            )[0];
          if (!pick) continue;
          pick.hFacings += 1;
          skuTotals.set(pick.skuId, (skuTotals.get(pick.skuId) ?? 0) + 1);
          laneTotal.set(shelfRank, (laneTotal.get(shelfRank) ?? 0) + pick.w);
          extraGrown.set(entry.chunk, (extraGrown.get(entry.chunk) ?? 0) + pick.w);
          grewAny = true;
          break;
        }
      });
      if (!grewAny) break;
      placed = computePlacements();
    }
    {
      const laneEnd = new Map<number, number>();
      for (const entry of placed) {
        const hi = entry.x + chunkWidth(entry.chunk) * entry.squeeze;
        laneEnd.set(entry.shelfRank, Math.max(laneEnd.get(entry.shelfRank) ?? 0, hi));
      }
      laneEnd.forEach((end, shelfRank) => {
        const deficit = run.widthCm - end;
        if (deficit > 12) {
          residuals.push({
            blockKey: `run edge · shelf ${shelfRank}`,
            deficitCm: +deficit.toFixed(1),
            reason: "cap",
          });
        }
      });
    }
    for (const entry of placed) {
      let x = entry.x;
      for (const pack of entry.chunk.packs) {
        place(pack.skuId, run.run, entry.shelfRank, x, pack.hFacings);
        x += pack.w * pack.hFacings * entry.squeeze;
      }
    }

    blocks.forEach((block) => {
      if (block.chunks.size < 2) return;
      const maxPack = Math.max(
        ...[...block.chunks.values()].flatMap((chunk) => chunk.packs.map((pack) => pack.w)),
        0,
      );
      const deviation = deviationOf.get(block.key) ?? 0;
      if (deviation > maxPack / 2 + 0.5) {
        residuals.push({
          blockKey: block.key,
          deficitCm: +deviation.toFixed(1),
          reason: "cap",
        });
      }
    });
  }
  const fixed: GeneratedLayout = {
    placements,
    walkOrder: layout.walkOrder,
    ceilingViolations: layout.ceilingViolations,
  };

  const after = facingsOf(fixed);
  const grown: VerticalBlockingGrowth[] = [];
  const shrunk: VerticalBlockingShrink[] = [];
  const allSkus = new Set([...before.keys(), ...after.keys()]);
  allSkus.forEach((skuId) => {
    if (allUnranged.has(skuId)) return;
    const delta = (after.get(skuId) ?? 0) - (before.get(skuId) ?? 0);
    const item = bySku.get(skuId);
    const run = item?.run ?? 0;
    const shelf = placements[skuId]?.segments[0]?.shelf ?? 0;
    if (delta > 0) grown.push({ skuId, run, shelf, addedFacings: delta });
    else if (delta < 0) shrunk.push({ skuId, run, shelf, removedFacings: -delta });
  });

  const raggedAfter = raggedness(blockLaneWidths(fixed, items, draft));

  const blockingBefore = blockingScoreOf(
    items,
    layout,
    runs,
    draft,
    options.rules,
    options.verticalBlockShare,
  );
  const blockingAfter = blockingScoreOf(
    items,
    fixed,
    runs,
    draft,
    options.rules,
    options.verticalBlockShare,
  );

  // Composite is rounded; a tie or NaN falls through to edges.
  const measurable = Number.isFinite(blockingBefore) && Number.isFinite(blockingAfter);
  const edgesBefore = blockEdgeSpread(layout, items, draft);
  const edgesAfter = blockEdgeSpread(fixed, items, draft);
  const edgesNoWorse = edgesAfter <= edgesBefore + 0.5;
  const verdict = !measurable
    ? edgesNoWorse
    : blockingAfter > blockingBefore + 1e-6
      ? true
      : blockingAfter < blockingBefore - 1e-6
        ? false
        : edgesNoWorse;
  if (onlyIfBetter && !verdict) {
    return {
      layout,
      unranged: [],
      grown: [],
      shrunk: [],
      residuals: [
        {
          blockKey: "whole plan",
          deficitCm: +(blockingBefore - blockingAfter).toFixed(1),
          reason: "band",
        },
        ...residuals,
      ],
      raggedBefore,
      raggedAfter: raggedBefore,
      scoreBefore: blockingBefore,
      scoreAfter: blockingAfter,
    };
  }

  return {
    layout: fixed,
    unranged: [...allUnranged],
    grown,
    shrunk,
    residuals,
    raggedBefore,
    raggedAfter,
    scoreBefore: blockingBefore,
    scoreAfter: blockingAfter,
  };
};
